import { useMutation } from '@apollo/client';
import { REMOVE_VEHICLE } from '@mutations/removeVehicle';
import { GET_CUSTOMER } from '@queries/getCustomer';
import { BaseText, Button, Title, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import VehicleAvatar from 'components/common/VehicleAvatar';
import useAfterVehicleRedirect from 'hooks/useAfterVehicleRedirect';
import usePurchasesForVehicle from 'hooks/usePurchasesForVehicle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { parkingVehicleLicensePlate } from 'states/common';
import { useParkingStore } from 'stores/parkingStore';
import { useVehicleStore } from 'stores/vehicleStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  Mutation,
  MutationDeleteVehicleArgs,
} from 'types/generatedSchemaTypes';
import { evictFromCache } from 'utils/cacheUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import VehicleRemovalBlocked from './VehicleRemovalBlocked';
import { useUiStore } from 'stores/uiStore';

const ConfirmVehicleRemoval: React.FC = () => {
  const { t } = useTranslation();
  const { setParkingVehiclePlate } = useParkingStore((state) => state);
  const { setIsDeleted, vehicleData: selectedVehicle } = useVehicleStore(
    (state) => state
  );
  const { blockVehicleDelete } = usePurchasesForVehicle(
    selectedVehicle?.licensePlate?.plateText
  );
  const { navigateAfterVehicleSaved } = useAfterVehicleRedirect();

  const [removeVehicle, { loading }] = useMutation<
    Mutation,
    MutationDeleteVehicleArgs
  >(REMOVE_VEHICLE, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_CUSTOMER,
        variables: {
          input: {
            fetchDigitalPermits: true,
          },
        },
        errorPolicy: 'all',
      },
    ],
    variables: {
      input: {
        id: selectedVehicle.id || selectedVehicle?.licensePlate?.plateText,
      },
    },
    onCompleted: () => {
      navigateAfterVehicleSaved();
      useUiStore.getState().setSheetModal('');
    },
    onError: () => showToast('error.vehicleRemoving', 'error'),
    update: (cache, deleteData) => {
      parkingVehicleLicensePlate(undefined);
      setParkingVehiclePlate(undefined);
      //Have to remove the vehicle from cache after navigating to stop the EditVehicle rerender
      setTimeout(
        () =>
          evictFromCache(cache, {
            __typename: 'Vehicle',
            id: deleteData?.data?.deleteVehicle?.id,
          }),
        1500
      );
    },
  });

  const handleRemoveButtonPressed = () => {
    setIsDeleted(true);
    removeVehicle();
  };

  if (blockVehicleDelete) {
    return <VehicleRemovalBlocked />;
  }

  return (
    <StyledWrapper mb={0}>
      <StyledWrapper maxWidth={300} center>
        <StyledWrapper maxWidth={350} center mb={20}>
          <VehicleAvatar
            color={selectedVehicle?.appSettings?.color}
            icon={selectedVehicle?.appSettings?.icon}
            size="xxl"
          />
        </StyledWrapper>
        <StyledWrapper maxWidth={350} center mb={6}>
          <Title center {...testIdentifiers('remove-vehicle-license')}>
            {selectedVehicle?.licensePlate?.plateText}
          </Title>
        </StyledWrapper>
        <StyledWrapper maxWidth={350} center mb={65}>
          <BaseText
            center
            {...testIdentifiers('remove-vehicle-name')}
            color={theme.colors.gray300}>
            {selectedVehicle?.name}
          </BaseText>
        </StyledWrapper>
      </StyledWrapper>
      <StyledWrapper mb={23}>
        <Button
          loading={loading}
          {...testIdentifiers('remove-vehicle-button')}
          size="l"
          buttonType="alert"
          text={loading ? t('button.removing') : t('button.remove')}
          onPress={() => handleRemoveButtonPressed()}
          disabled={loading}
        />
      </StyledWrapper>
    </StyledWrapper>
  );
};

export default ConfirmVehicleRemoval;
